import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import { SlideObject } from '../types';
import { PRES } from '../chrome';
import { COPY } from '../presentationCopy';
import { ResizeHandle, rotatedAabb, rotateLocalPoint, uncroppedImageRect } from './objectTransform';

interface SelectionChromeProps {
  object: SlideObject;
  scale: number;
  onHandle: (event: React.PointerEvent, handle: ResizeHandle, crop: boolean) => void;
  onRotate: (event: React.PointerEvent) => void;
  onMove?: (event: React.PointerEvent) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onToggleLock: () => void;
  hideSideHandles?: boolean;
  hideResizeHandles?: boolean;
  showTextFormat?: boolean;
  onTextStyle?: (patch: Record<string, unknown>) => void;
  onToggleMark?: (mark: 'bold' | 'italic' | 'underline') => void;
  queryTextMark?: (mark: 'bold' | 'italic' | 'underline') => boolean | null;
  showAi?: boolean;
  aiBusy?: boolean;
  onAiRewrite?: () => void;
  onAiGrammar?: () => void;
  onAiTranslate?: () => void;
  showLock?: boolean;
  onEditText?: () => void;
  onFitImage?: () => void;
}

const HANDLES: Array<{ key: ResizeHandle; cursor: string; left: string; top: string; corner: boolean }> = [
  { key: 'nw', cursor: 'nwse-resize', left: '0%', top: '0%', corner: true },
  { key: 'n', cursor: 'ns-resize', left: '50%', top: '0%', corner: false },
  { key: 'ne', cursor: 'nesw-resize', left: '100%', top: '0%', corner: true },
  { key: 'e', cursor: 'ew-resize', left: '100%', top: '50%', corner: false },
  { key: 'se', cursor: 'nwse-resize', left: '100%', top: '100%', corner: true },
  { key: 's', cursor: 'ns-resize', left: '50%', top: '100%', corner: false },
  { key: 'sw', cursor: 'nesw-resize', left: '0%', top: '100%', corner: true },
  { key: 'w', cursor: 'ew-resize', left: '0%', top: '50%', corner: false },
];

const SelectionChrome: React.FC<SelectionChromeProps> = ({
  object, scale, onHandle, onRotate, onMove, onDuplicate, onDelete, onToggleLock, hideSideHandles,
  hideResizeHandles, showTextFormat, onTextStyle, onToggleMark, queryTextMark, showAi,
  aiBusy, onAiRewrite, onAiGrammar, onAiTranslate, showLock,
  onEditText, onFitImage,
}) => {
  const locked = object.locks.position && object.locks.size;
  const size = 11 / scale;
  const pillW = 22 / scale;
  const pillH = 8 / scale;
  const rotation = object.style?.rotation || 0;
  const isMedia = object.type === 'image' || object.type === 'product';

  return (
    <>
      <Box sx={{
        position: 'absolute',
        inset: -1,
        border: `${2 / scale}px solid ${PRES.gold}`,
        pointerEvents: 'none',
        borderRadius: 0.25,
      }} />
      {onMove && !object.locks.position && (
        <>
          <Box onPointerDown={onMove} sx={{ position: 'absolute', left: 8, right: 8, top: 0, height: 10 / scale, transform: 'translateY(-50%)', cursor: 'move', zIndex: 3 }} />
          <Box onPointerDown={onMove} sx={{ position: 'absolute', left: 8, right: 8, bottom: 0, height: 10 / scale, transform: 'translateY(50%)', cursor: 'move', zIndex: 3 }} />
          <Box onPointerDown={onMove} sx={{ position: 'absolute', top: 8, bottom: 8, left: 0, width: 10 / scale, transform: 'translateX(-50%)', cursor: 'move', zIndex: 3 }} />
          <Box onPointerDown={onMove} sx={{ position: 'absolute', top: 8, bottom: 8, right: 0, width: 10 / scale, transform: 'translateX(50%)', cursor: 'move', zIndex: 3 }} />
        </>
      )}
      {!hideResizeHandles && !object.locks.size && HANDLES.filter((handle) => handle.corner || !hideSideHandles).map((handle) => {
        const vertical = handle.key === 'e' || handle.key === 'w';
        const visualW = handle.corner ? size : (vertical ? pillH : pillW);
        const visualH = handle.corner ? size : (vertical ? pillW : pillH);
        return (
        <Box
          key={handle.key}
          onPointerDown={(event) => {
            event.stopPropagation();
            onHandle(event, handle.key, !handle.corner && isMedia);
          }}
          sx={{
            position: 'absolute',
            left: handle.left,
            top: handle.top,
            width: Math.max(visualW, 22 / scale),
            height: Math.max(visualH, 22 / scale),
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: handle.cursor,
            zIndex: 4,
          }}
        >
          <Box
            sx={{
              width: visualW,
              height: visualH,
              bgcolor: PRES.white,
              border: `${1.5 / scale}px solid ${PRES.navy}`,
              borderRadius: handle.corner ? '50%' : 8,
              boxShadow: '0 1px 4px rgba(10,39,87,0.2)',
              pointerEvents: 'none',
            }}
          />
        </Box>
        );
      })}
      {!object.locks.position && (
        <Box
          onPointerDown={(event) => {
            event.stopPropagation();
            onRotate(event);
          }}
          title="Rotera"
          sx={{
            position: 'absolute',
            left: '50%',
            top: `calc(100% + ${18 / scale}px)`,
            width: 22 / scale,
            height: 22 / scale,
            transform: `translateX(-50%) rotate(${-rotation}deg)`,
            bgcolor: PRES.white,
            border: `${1.5 / scale}px solid ${PRES.navy}`,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'grab',
            boxShadow: '0 4px 12px rgba(10,39,87,0.16)',
            zIndex: 5,
          }}
        >
          <ReplayOutlinedIcon sx={{ fontSize: 14 / scale, color: PRES.navy }} />
        </Box>
      )}
      <Box
        onPointerDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={(event) => event.stopPropagation()}
        sx={{
          position: 'absolute',
          left: '50%',
          bottom: `calc(100% + ${14 / scale}px)`,
          transform: `translateX(-50%) rotate(${-rotation}deg)`,
          display: 'flex',
          alignItems: 'center',
          gap: 0.15,
          bgcolor: PRES.white,
          border: `1px solid ${PRES.line}`,
          borderRadius: 5,
          px: 0.4,
          py: 0.15,
          boxShadow: '0 10px 24px rgba(10,39,87,0.16)',
          zIndex: 6,
          fontFamily: 'Inter, sans-serif',
          fontSize: `${16 / scale}px`,
          fontWeight: 400,
          letterSpacing: 0,
          lineHeight: 1,
          textTransform: 'none',
          '& .MuiIconButton-root': { padding: `${5 / scale}px` },
          '& .MuiSvgIcon-root': { fontSize: `${16 / scale}px` },
        }}
      >
        {showTextFormat && (onToggleMark || onTextStyle) && (
          <>
            <FormatMarkButton
              title="Fet"
              active={queryTextMark?.('bold') === true}
              onClick={() => {
                if (onToggleMark) onToggleMark('bold');
                else onTextStyle?.({ fontWeight: 600 });
              }}
            >
              <FormatBoldIcon sx={{ fontSize: 16 }} />
            </FormatMarkButton>
            <FormatMarkButton
              title="Kursiv"
              active={queryTextMark?.('italic') === true}
              onClick={() => {
                if (onToggleMark) onToggleMark('italic');
                else onTextStyle?.({ fontStyle: 'italic' });
              }}
            >
              <FormatItalicIcon sx={{ fontSize: 16 }} />
            </FormatMarkButton>
            <FormatMarkButton
              title="Understruken"
              active={queryTextMark?.('underline') === true}
              onClick={() => {
                if (onToggleMark) onToggleMark('underline');
                else onTextStyle?.({ textDecoration: 'underline' });
              }}
            >
              <FormatUnderlinedIcon sx={{ fontSize: 16 }} />
            </FormatMarkButton>
            <Box sx={{ width: 1, height: 16, bgcolor: PRES.line, mx: 0.2 }} />
          </>
        )}
        {showAi && (onAiRewrite || onAiGrammar || onAiTranslate) && (
          <>
            {onAiRewrite && (
              <Tooltip title={aiBusy ? COPY.aiRewriteBusy : COPY.aiRewrite}>
                <span>
                  <IconButton
                    size="small"
                    disabled={aiBusy}
                    onPointerDown={(event) => event.preventDefault()}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={onAiRewrite}
                    sx={{ color: PRES.navy, borderRadius: 1, px: 0.6, fontSize: `${12 / scale}px`, fontWeight: 650, opacity: aiBusy ? 0.55 : 1 }}
                  >
                    {aiBusy ? COPY.aiRewriteBusy : COPY.aiRewrite}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {onAiGrammar && (
              <Tooltip title={aiBusy ? COPY.aiGrammarBusy : COPY.aiGrammar}>
                <span>
                  <IconButton
                    size="small"
                    disabled={aiBusy}
                    onPointerDown={(event) => event.preventDefault()}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={onAiGrammar}
                    sx={{ color: PRES.navy, borderRadius: 1, px: 0.6, fontSize: `${12 / scale}px`, fontWeight: 650, opacity: aiBusy ? 0.55 : 1 }}
                  >
                    {aiBusy ? COPY.aiGrammarBusy : COPY.aiGrammar}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {onAiTranslate && (
              <Tooltip title={aiBusy ? COPY.aiTranslateBusy : COPY.aiTranslate}>
                <span>
                  <IconButton
                    size="small"
                    disabled={aiBusy}
                    onPointerDown={(event) => event.preventDefault()}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={onAiTranslate}
                    sx={{ color: PRES.navy, borderRadius: 1, px: 0.6, fontSize: `${12 / scale}px`, fontWeight: 650, opacity: aiBusy ? 0.55 : 1 }}
                  >
                    {aiBusy ? COPY.aiTranslateBusy : COPY.aiTranslate}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Box sx={{ width: 1, height: 16, bgcolor: PRES.line, mx: 0.2 }} />
          </>
        )}
        {onEditText && (
          <Tooltip title={COPY.textInShape}>
            <IconButton size="small" onClick={onEditText} sx={{ color: PRES.navy }}>
              <Box
                component="span"
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  border: `1.5px solid ${PRES.navy}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 10,
                  fontWeight: 700,
                  lineHeight: 1,
                  fontFamily: 'Kapra Neue, Georgia, serif',
                }}
              >
                A
              </Box>
            </IconButton>
          </Tooltip>
        )}
        {showLock && (
          <Tooltip title={locked ? 'Lås upp' : 'Lås'}>
            <IconButton size="small" onClick={onToggleLock} sx={{ color: PRES.navy }}>
              {locked ? <LockOutlinedIcon sx={{ fontSize: 16 }} /> : <LockOpenOutlinedIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
        )}
        {onFitImage && (
          <Tooltip title={COPY.fitImageInFrame}>
            <IconButton size="small" onClick={onFitImage} sx={{ color: PRES.navy }}>
              <AspectRatioIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Duplicera">
          <IconButton size="small" onClick={onDuplicate} disabled={object.locks.delete} sx={{ color: PRES.navy }}>
            <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Radera">
          <IconButton size="small" onClick={onDelete} disabled={object.locks.delete} sx={{ color: PRES.navy }}>
            <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </>
  );
};

const FormatMarkButton: React.FC<{
  title: string;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ title, active, onClick, children }) => (
  <Tooltip title={title}>
    <IconButton
      size="small"
      onPointerDown={(event) => event.preventDefault()}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      sx={{ color: active ? PRES.white : PRES.navy, bgcolor: active ? PRES.navy : 'transparent', borderRadius: 1 }}
    >
      {children}
    </IconButton>
  </Tooltip>
);

export function ImageScaleHandles({
  object,
  scale,
  onHandle,
}: {
  object: SlideObject;
  scale: number;
  onHandle: (event: React.PointerEvent, handle: ResizeHandle) => void;
}) {
  const img = uncroppedImageRect(object);
  const size = 13 / scale;
  const hit = 24 / scale;
  const rotation = object.style?.rotation || 0;
  const corners = [
    { key: 'nw' as ResizeHandle, left: img.x, top: img.y, cursor: 'nwse-resize' },
    { key: 'ne' as ResizeHandle, left: img.x + img.w, top: img.y, cursor: 'nesw-resize' },
    { key: 'se' as ResizeHandle, left: img.x + img.w, top: img.y + img.h, cursor: 'nwse-resize' },
    { key: 'sw' as ResizeHandle, left: img.x, top: img.y + img.h, cursor: 'nesw-resize' },
  ].map((corner) => {
    const point = rotateLocalPoint(object.x, object.y, object.w, object.h, corner.left, corner.top, rotation);
    return { key: corner.key, left: point.x, top: point.y, cursor: corner.cursor };
  });
  return (
    <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 12000 }}>
      {corners.map((corner) => (
        <Box
          key={corner.key}
          onPointerDown={(event) => {
            event.stopPropagation();
            onHandle(event, corner.key);
          }}
          title="Skala hela bilden"
          sx={{
            position: 'absolute',
            left: corner.left,
            top: corner.top,
            width: hit,
            height: hit,
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: corner.cursor,
            zIndex: 9,
            pointerEvents: 'auto',
          }}
        >
          <Box
            sx={{
              width: size,
              height: size,
              bgcolor: PRES.white,
              border: `${2 / scale}px solid ${PRES.gold}`,
              borderRadius: '50%',
              boxShadow: '0 1px 4px rgba(10,39,87,0.25)',
              pointerEvents: 'none',
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

export function CropSessionBar({
  object,
  scale,
  onReset,
  onDone,
}: {
  object: SlideObject;
  scale: number;
  onReset: () => void;
  onDone: () => void;
}) {
  const rotation = object.style?.rotation || 0;
  const aabb = rotatedAabb(object.x, object.y, object.w, object.h, rotation);
  return (
    <Box
      sx={{
        position: 'absolute',
        left: aabb.x + aabb.w / 2,
        top: aabb.y - 36,
        transform: 'translateX(-50%)',
        bgcolor: PRES.white,
        border: `1px solid ${PRES.line}`,
        borderRadius: 5,
        px: 1,
        py: 0.4,
        display: 'flex',
        gap: 1,
        boxShadow: '0 10px 24px rgba(10,39,87,0.16)',
        zIndex: 12000,
        pointerEvents: 'auto',
        whiteSpace: 'nowrap',
        fontFamily: 'Inter, sans-serif',
        fontSize: `${12 / scale}px`,
        fontWeight: 400,
        letterSpacing: 0,
        lineHeight: 1.4,
        textTransform: 'none',
        color: PRES.navy,
      }}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <Box component="span" sx={{ opacity: 0.7 }}>Beskär med sidohandtagen, skala med hörnen på hela bilden, dra i rutan</Box>
      <Box
        component="button"
        onClick={(event) => {
          event.stopPropagation();
          onReset();
        }}
        style={{ border: 0, background: 'none', cursor: 'pointer', color: '#1B3967', fontWeight: 600 }}
      >
        Återställ
      </Box>
      <Box
        component="button"
        onClick={(event) => {
          event.stopPropagation();
          onDone();
        }}
        style={{ border: 0, background: '#1B3967', color: '#fff', borderRadius: 999, padding: '2px 10px', cursor: 'pointer', fontWeight: 600 }}
      >
        Klart
      </Box>
    </Box>
  );
}

export function ScreenAlignedResizeHandles({
  object,
  scale,
  onHandle,
  cornersOnly = false,
}: {
  object: SlideObject;
  scale: number;
  onHandle: (event: React.PointerEvent, handle: ResizeHandle) => void;
  cornersOnly?: boolean;
}) {
  const rotation = object.style?.rotation || 0;
  const aabb = rotatedAabb(object.x, object.y, object.w, object.h, rotation);
  const size = 11 / scale;
  const pillW = 22 / scale;
  const pillH = 8 / scale;
  const handles = (
    [
      { key: 'nw' as ResizeHandle, left: aabb.x, top: aabb.y, cursor: 'nwse-resize', width: size, height: size, corner: true },
      { key: 'ne' as ResizeHandle, left: aabb.x + aabb.w, top: aabb.y, cursor: 'nesw-resize', width: size, height: size, corner: true },
      { key: 'se' as ResizeHandle, left: aabb.x + aabb.w, top: aabb.y + aabb.h, cursor: 'nwse-resize', width: size, height: size, corner: true },
      { key: 'sw' as ResizeHandle, left: aabb.x, top: aabb.y + aabb.h, cursor: 'nesw-resize', width: size, height: size, corner: true },
      { key: 'n' as ResizeHandle, left: aabb.x + aabb.w / 2, top: aabb.y, cursor: 'ns-resize', width: pillW, height: pillH, corner: false },
      { key: 's' as ResizeHandle, left: aabb.x + aabb.w / 2, top: aabb.y + aabb.h, cursor: 'ns-resize', width: pillW, height: pillH, corner: false },
      { key: 'e' as ResizeHandle, left: aabb.x + aabb.w, top: aabb.y + aabb.h / 2, cursor: 'ew-resize', width: pillH, height: pillW, corner: false },
      { key: 'w' as ResizeHandle, left: aabb.x, top: aabb.y + aabb.h / 2, cursor: 'ew-resize', width: pillH, height: pillW, corner: false },
    ]
  ).filter((handle) => handle.corner || !cornersOnly);
  return (
    <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 11900 }}>
      {handles.map((handle) => (
        <Box
          key={handle.key}
          onPointerDown={(event) => {
            event.stopPropagation();
            onHandle(event, handle.key);
          }}
          sx={{
            position: 'absolute',
            left: handle.left,
            top: handle.top,
            width: Math.max(handle.width, 22 / scale),
            height: Math.max(handle.height, 22 / scale),
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: handle.cursor,
            pointerEvents: 'auto',
          }}
        >
          <Box
            sx={{
              width: handle.width,
              height: handle.height,
              bgcolor: PRES.white,
              border: `${1.5 / scale}px solid ${PRES.navy}`,
              borderRadius: handle.corner ? '50%' : 8,
              boxShadow: '0 1px 4px rgba(10,39,87,0.2)',
              pointerEvents: 'none',
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

export function ScreenAlignedCropHandles({
  object,
  scale,
  onHandle,
}: {
  object: SlideObject;
  scale: number;
  onHandle: (event: React.PointerEvent, handle: ResizeHandle) => void;
}) {
  const rotation = object.style?.rotation || 0;
  const aabb = rotatedAabb(object.x, object.y, object.w, object.h, rotation);
  const pillW = 22 / scale;
  const pillH = 8 / scale;
  const sides: Array<{ key: ResizeHandle; left: number; top: number; cursor: string; width: number; height: number }> = [
    { key: 'n', left: aabb.x + aabb.w / 2, top: aabb.y, cursor: 'ns-resize', width: pillW, height: pillH },
    { key: 's', left: aabb.x + aabb.w / 2, top: aabb.y + aabb.h, cursor: 'ns-resize', width: pillW, height: pillH },
    { key: 'e', left: aabb.x + aabb.w, top: aabb.y + aabb.h / 2, cursor: 'ew-resize', width: pillH, height: pillW },
    { key: 'w', left: aabb.x, top: aabb.y + aabb.h / 2, cursor: 'ew-resize', width: pillH, height: pillW },
  ];
  return (
    <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 12000 }}>
      {Boolean(rotation) && (
        <Box sx={{
          position: 'absolute',
          left: aabb.x,
          top: aabb.y,
          width: aabb.w,
          height: aabb.h,
          border: `${1.5 / scale}px dashed ${PRES.gold}`,
          pointerEvents: 'none',
        }} />
      )}
      {sides.map((side) => (
        <Box
          key={side.key}
          onPointerDown={(event) => {
            event.stopPropagation();
            onHandle(event, side.key);
          }}
          sx={{
            position: 'absolute',
            left: side.left,
            top: side.top,
            width: Math.max(side.width, 22 / scale),
            height: Math.max(side.height, 22 / scale),
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: side.cursor,
            pointerEvents: 'auto',
          }}
        >
          <Box
            sx={{
              width: side.width,
              height: side.height,
              bgcolor: PRES.white,
              border: `${1.5 / scale}px solid ${PRES.navy}`,
              borderRadius: 8,
              boxShadow: '0 1px 4px rgba(10,39,87,0.2)',
              pointerEvents: 'none',
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

export default SelectionChrome;
